/**
 * RoadmapPage - public roadmap for NLT Radio
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Circle } from 'lucide-react';
import Footer from '../components/Footer';

const phases = [
  {
    n: 1,
    title: 'Launch',
    status: 'live',
    text: 'NLT Radio officially launches — artist profiles, live rooms, voting and rotation, Stories, and direct messaging, now live.',
  },
  {
    n: 2,
    title: 'Merch Store Beta',
    status: 'upcoming',
    text: 'Full merch store live in beta, giving artists a real storefront to sell products directly to fans.',
  },
  {
    n: 3,
    title: 'Tiny Desk Studio',
    status: 'upcoming',
    text: 'An intimate, curated live performance feature — stripped-down sessions that put the artist and the music front and center.',
  },
  {
    n: 4,
    title: 'Curated Playlists',
    status: 'upcoming',
    text: 'Hand-picked and algorithm-driven playlists highlighting rising artists and top tracks across every genre.',
  },
  {
    n: 5,
    title: 'Event Locator',
    status: 'upcoming',
    text: 'Discover live shows, performances, and events from artists on the platform — connecting the digital experience to real-world stages.',
  },
  {
    n: 6,
    title: 'Beta Testing & Full App Launch',
    status: 'upcoming',
    text: 'Open beta testing period, gathering real user feedback before full public launch.',
  },
  {
    n: 7,
    title: '500 User Milestone → App Store & Google Play',
    status: 'upcoming',
    text: 'Once the platform reaches 500 active users, NLT Radio officially launches on the App Store and Google Play.',
  },
];

const RoadmapPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="roadmap-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="roadmap-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <span className="text-white font-semibold">Roadmap</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF007F]/10 via-transparent to-[#00F0FF]/10 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Unbounded' }}>
            The <span className="bg-gradient-to-r from-[#00F0FF] to-[#FF007F] bg-clip-text text-transparent">Roadmap</span>
          </h1>
          <p className="text-[#A1A1AA] text-base mt-4 max-w-lg mx-auto">
            Where NLT Radio is today and where we're going. Built in the open, one phase at a time.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-2xl mx-auto px-5 py-6">
        <div className="relative">
          {/* vertical line */}
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-white/10" />

          <div className="space-y-6">
            {phases.map((p, i) => {
              const isLive = p.status === 'live';
              return (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="relative flex gap-4"
                  data-testid={`roadmap-phase-${p.n}`}
                >
                  {/* node */}
                  <div className="relative z-10 shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                      isLive
                        ? 'bg-gradient-to-br from-[#00F0FF] to-[#FF007F] border-transparent shadow-[0_0_20px_rgba(0,240,255,0.4)]'
                        : 'bg-white/5 border-white/15'
                    }`}>
                      {isLive ? <Check className="w-5 h-5 text-white" /> : <Circle className="w-3 h-3 text-white/40" />}
                    </div>
                  </div>

                  {/* card */}
                  <div className="flex-1 rounded-2xl bg-white/5 border border-white/10 p-4 mb-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="text-[#71717A] text-xs font-medium">Phase {p.n}</span>
                      {isLive ? (
                        <span className="px-2 py-0.5 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] text-[11px] font-bold uppercase tracking-wide flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" /> Live
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-[11px] font-medium uppercase tracking-wide">Upcoming</span>
                      )}
                    </div>
                    <h3 className="text-white font-semibold text-lg">{p.title}</h3>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed mt-1">{p.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RoadmapPage;
